import Cookies from "js-cookie";

const TOKEN_KEY = "token";

/**
 * Per-tab auth token helpers.
 *
 * sessionStorage holds this tab's token so different accounts can run in
 * parallel tabs. localStorage keeps the last-used token so a newly opened
 * tab can inherit a session once.
 *
 * The `token` cookie is shared across tabs and is also sent on Socket.IO
 * WebSocket handshakes. Keep it in sync with this tab's auth token before
 * every socket connect so backend cookie vs auth.token checks do not diverge.
 */

const cookieOptions = () => {
  const isLocalhost = /^(localhost|127\.0\.0\.1)$/.test(
    window.location.hostname,
  );
  return {
    secure: !isLocalhost,
    sameSite: (isLocalhost ? "Lax" : "Strict") as "Lax" | "Strict",
    path: "/",
  };
};

//Ensure the shared cookie matches the JWT this tab will send as socket.auth.
export const syncAuthCookie = (token: string): void => {
  try {
    if (Cookies.get(TOKEN_KEY) === token) return;
    Cookies.set(TOKEN_KEY, token, cookieOptions());
  } catch {
    // ignore
  }
};

export const getAuthToken = (): string | null => {
  try {
    const sessionToken = sessionStorage.getItem(TOKEN_KEY);
    if (sessionToken) {
      return sessionToken;
    }

    const localToken = localStorage.getItem(TOKEN_KEY);
    if (localToken) {
      sessionStorage.setItem(TOKEN_KEY, localToken);
      return localToken;
    }

    return null;
  } catch {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }
};

export const setAuthToken = (token: string): void => {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore
  }
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // ignore
  }
  syncAuthCookie(token);
};

export const clearAuthToken = (): void => {
  let sessionToken: string | null = null;
  try {
    sessionToken = sessionStorage.getItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }

  let clearedLocal = false;
  try {
    const localToken = localStorage.getItem(TOKEN_KEY);
    if (!sessionToken || localToken === sessionToken) {
      localStorage.removeItem(TOKEN_KEY);
      clearedLocal = true;
    }
  } catch {
    // ignore
  }

  try {
    const cookieToken = Cookies.get(TOKEN_KEY);
    if (!cookieToken) return;
    // Drop shared cookie when it belongs to this tab, or no other tab token remains.
    if (cookieToken === sessionToken || clearedLocal) {
      Cookies.remove(TOKEN_KEY, { path: "/" });
    }
  } catch {
    // ignore
  }
};

export const hasAuthToken = (): boolean => !!getAuthToken();

// Best-effort user id from JWT payload (no signature verification).
export const getUserIdFromToken = (token?: string | null): string | null => {
  const value = token === undefined ? getAuthToken() : token;
  if (!value) return null;

  try {
    const payloadPart = value.split(".")[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(normalized)) as {
      user_id?: string;
      sub?: string;
    };
    return payload.user_id || payload.sub || null;
  } catch {
    return null;
  }
};
